import { ChartBar } from "../components/ChartOverlayDrawer";
import { escapeHtml } from "../../MapUtil";
import { getPalette } from "../Palette";

// --- レイアウト定数 ---
const CYL_R = 12; // 円柱の半径（px）
const ELP_RY = 6; // 楕円の縦半径（3D感）
const MAX_CYL_H = 80; // globalMax 時の円柱総高さ（px）
const AREA_LABEL_H = 18; // エリア名ラベルの高さ
const SVG_W = CYL_R * 2 + 8;
const CX = SVG_W / 2;
const BASELINE_Y = MAX_CYL_H + ELP_RY; // SVG 内のベースライン Y
const SVG_H = BASELINE_Y + AREA_LABEL_H;

export const useCylinderChart = () => {
  const f = (n: number) => n.toFixed(1);

  /**
   * 積み上げグラフを生成する
   */
  const createCylinderChartSvg = (
    bars: ChartBar[],
    areaName: string,
    globalMax: number,
    isSelected: boolean,
  ): { svg: string; iconW: number; iconH: number; anchorX: number; anchorY: number } => {
    // 合計値とそれをもとに全体の高さを計算
    const totalVal = bars.reduce((s, b) => s + b.count, 0);
    const totalH = globalMax > 0 ? (totalVal / globalMax) * MAX_CYL_H : 0;

    const escapedArea = escapeHtml(areaName);
    const areaLabel = `<text x="${f(CX)}" y="${f(SVG_H - 2)}" class="chart-label chart-bar-title" >${escapedArea}</text>`;

    if (totalH < 0.5) {
      const empty = `<ellipse cx="${f(CX)}" cy="${f(BASELINE_Y)}" rx="${CYL_R}" ry="${ELP_RY}" fill="#aaaaaa"/>`;
      return {
        svg: `<svg xmlns="http://www.w3.org/2000/svg" width="${SVG_W}" height="${SVG_H}" overflow="visible">${empty}${areaLabel}</svg>`,
        iconW: SVG_W,
        iconH: SVG_H,
        anchorX: CX,
        anchorY: BASELINE_Y,
      };
    }

    // セグメントを下から上へ積み上げ
    const segments: string[] = [];
    let yBottom = BASELINE_Y;

    for (let i = 0; i < bars.length; i++) {
      const bar = bars[i];
      const segH = globalMax > 0 ? (bar.count / globalMax) * MAX_CYL_H : 0;
      if (segH < 0.5) continue;

      const palette = getPalette(bar.label.id);
      const yTop = yBottom - segH;

      // 下側楕円を描画して、積み上げ境界の立体感を維持する
      const bottomEllipse = `<ellipse cx="${f(CX)}" cy="${f(yBottom)}" rx="${CYL_R}" ry="${ELP_RY}" fill="${palette.front}"/>`;
      // 胴体（矩形で近似）+ ツールチップ用 title
      const escapedLabel = escapeHtml(bar.label.name);
      const escapedLabelAttr = escapeHtml(bar.label.name);
      const body = `<rect x="${f(CX - CYL_R)}" y="${f(yTop)}" width="${CYL_R * 2}" height="${f(segH)}" fill="${palette.front}"><title>${escapedLabel}(${bar.count})</title></rect>`;
      // 上面楕円（3D感）
      const topEllipse = `<ellipse cx="${f(CX)}" cy="${f(yTop)}" rx="${CYL_R}" ry="${ELP_RY}" fill="${palette.top}"/>`;
      // セグメント中央に値ラベル
      const midY = yTop + segH / 2 + ELP_RY / 2;
      const valLabel =
        segH >= 12
          ? `<text x="${f(CX)}" y="${f(midY)}" class="chart-label">${bar.count}</text>`
          : "";

      // data-* 属性を <g> に移動→上面楕円クリック時も closest で拾える
      segments.push(
        `<g data-label-id="${bar.label.id}" data-label-name="${escapedLabelAttr}" data-count="${bar.count}">${bottomEllipse}${body}${topEllipse}${valLabel}</g>`,
      );
      yBottom = yTop;
    }

    // 選択中の棒表示
    const outerStyle = isSelected ? ` class="selected-bar"` : "";
    const stackedGroup = `<g${outerStyle}>${segments.join("")}</g>`;

    // 合計値を円柱上部に表示（選択時は scale(1.25) 分だけ上に伸びるため補正）
    const scaleExtra = isSelected ? totalH * 0.25 : 0;
    const topY = BASELINE_Y - totalH - scaleExtra - ELP_RY - 3;
    const totalLabel = `<text x="${f(CX)}" y="${f(topY)}" class="chart-label chart-total">${totalVal}</text>`;

    return {
      svg: `<svg xmlns="http://www.w3.org/2000/svg" width="${SVG_W}" height="${SVG_H}" class="chart">
      ${stackedGroup}${totalLabel}${areaLabel} 
    </svg>`,
      iconW: SVG_W,
      iconH: SVG_H,
      anchorX: CX,
      anchorY: BASELINE_Y,
    };
  };

  return { createCylinderChartSvg };
};
